import axios from 'axios'
import axiosInstance, { endpoints } from '@/lib/axios'

// ----------------------------------------------------------------------
// R2 multipart upload client for the clip pipeline (Phase 4).
//
// This module is the SINGLE place where the client side of the presign /
// upload-session contract lives. The request/response shapes below were
// reconciled (Phase 4→3 static reconciliation) against the live rfc-club-api
// contract: AdminClipController upload routes + clip-upload-presign.dto.ts.
// They are no longer synthesized guesses — they mirror the API DTOs 1:1.
//
// Routes (all under POST /admin/clips/upload/*, admin-authed):
//   initiate        -> InitiateClipUploadDto      / InitiateClipUploadResponseDto
//   presign-parts   -> PresignClipPartsDto        / PresignClipPartsResponseDto  (BATCH)
//   complete        -> CompleteClipUploadDto       / CompleteClipUploadResponseDto
//   abort           -> AbortClipUploadDto          / void
//
// Transport split (see plan sequence §"Sequence — new clip"):
//   - initiate / presign-parts / complete / abort  -> rfc-club-api via
//     `axiosInstance` (admin-authed, baseURL = VITE_API_URL, 401 interceptor).
//   - the actual part PUTs + the thumbnail PUT  -> the presigned R2 URL via bare
//     `axios.put` (no interceptor, no baseURL) so we get per-part progress,
//     AbortController support, and the `ETag` response header.
//
// Thumbnail: the API presigns the thumbnail PUT in the *initiate* response
// (only when `thumbnailContentType` is sent). We upload it right after initiate
// while that presigned URL is freshest (it shares the ~900s TTL), before the
// long source-part loop can age it out.
// ----------------------------------------------------------------------

const R2_UPLOAD_ENDPOINTS = {
  initiate: `${endpoints.clips.create}/upload/initiate`,
  presignParts: `${endpoints.clips.create}/upload/presign-parts`,
  complete: `${endpoints.clips.create}/upload/complete`,
  abort: `${endpoints.clips.create}/upload/abort`,
} as const

// Number of part PUTs in flight at once. Bounded so the live Blob slices held by
// in-flight PUTs stay ~ (concurrency × partSize) and don't balloon memory, while
// still saturating the uplink and hiding per-part latency. (Plan: 4–8.)
const PART_UPLOAD_CONCURRENCY = 6

// How many parts we presign per API round-trip. The presign endpoint accepts an
// array, so we batch instead of one round-trip per part. Sized to a TTL (~900s)
// that comfortably covers a batch's uploads; a part whose URL is rejected/expired
// is re-presigned on its retry (batch-of-one) as the safety net.
const PRESIGN_BATCH_SIZE = 15

// Mirrors InitiateClipUploadDto. `thumbnailContentType` makes the API presign a
// thumbnail PUT and return `thumbnailUploadUrl` + `thumbnailInputKey`.
export type R2InitiateRequest = {
  filename: string
  contentType: string
  sizeBytes: number
  thumbnailContentType?: string
}

// Mirrors InitiateClipUploadResponseDto. The API reserves clipId + mediaToken +
// the multipart uploadId + the upload session, and decides the part size.
export type R2InitiateResponse = {
  uploadSessionId: string
  clipId: string
  mediaToken: string
  inputKey: string
  uploadId: string
  partSizeBytes: number
  thumbnailUploadUrl?: string
  thumbnailInputKey?: string
  expiresAt: string
}

// Mirrors PresignClipPartsDto / PresignClipPartsResponseDto (batched).
export type R2PresignPartsRequest = {
  uploadSessionId: string
  partNumbers: number[]
}
export type R2PresignedPart = {
  partNumber: number
  url: string
}
export type R2PresignPartsResponse = {
  parts: R2PresignedPart[]
}

// Mirrors CompletedPartInputDto / CompleteClipUploadDto.
export type R2UploadedPart = {
  partNumber: number
  etag: string
}
export type R2CompleteRequest = {
  uploadSessionId: string
  parts: R2UploadedPart[]
}

// Mirrors AbortClipUploadDto.
export type R2AbortRequest = {
  uploadSessionId: string
}

// Per-part progress snapshot surfaced to the UI.
export type R2PartProgress = {
  partNumber: number
  loaded: number
  total: number
  status: 'pending' | 'uploading' | 'done' | 'error'
}

export type R2UploadProgress = {
  /** 0-100 across the whole file */
  percent: number
  uploadedBytes: number
  totalBytes: number
  parts: R2PartProgress[]
}

// Result threaded into the create-clip payload (R2 mode). The API derives
// clipId + mediaToken from the consumed session, so the create payload only
// needs the session id + the keys it validates against the session.
export type R2UploadResult = {
  uploadSessionId: string
  inputKey: string
  thumbnailInputKey?: string
}

export type R2MultipartHandle = {
  /** Resolves when the full multipart upload completes (source + optional thumbnail). */
  done: Promise<R2UploadResult>
  /** Cancels in-flight part PUTs and calls the API abort endpoint (best-effort). */
  abort: () => Promise<void>
}

// ----------------------------------------------------------------------

function splitIntoParts(size: number, partSize: number): number {
  return Math.max(1, Math.ceil(size / partSize))
}

function extractEtag(headers: Record<string, unknown>): string {
  // R2/S3 returns the part ETag in the `ETag` response header (quoted).
  // CORS must expose ETag (Appendix C); if it is missing, completion will fail
  // loudly rather than silently — surfaced as an upload error to the user.
  const raw = (headers.etag ?? headers.ETag) as string | undefined
  if (!raw) {
    throw new Error('R2 part response did not expose an ETag header')
  }
  return raw.replace(/"/g, '')
}

function chunkPartNumbers(total: number, batchSize: number): number[][] {
  const batches: number[][] = []
  for (let start = 1; start <= total; start += batchSize) {
    const batch: number[] = []
    for (let n = start; n < start + batchSize && n <= total; n += 1) {
      batch.push(n)
    }
    batches.push(batch)
  }
  return batches
}

// ----------------------------------------------------------------------

/**
 * Drive a client-side R2 multipart upload for the clip source, plus the optional
 * admin thumbnail PUT. Returns a handle so the caller can await completion and
 * abort on unmount / user cancel.
 *
 * Speed model (Phase 7a):
 *   - parts upload through a bounded worker pool (`PART_UPLOAD_CONCURRENCY` in
 *     flight) instead of one-at-a-time, to saturate the uplink and hide per-part
 *     latency. Concurrency bounds the live Blob slices so memory stays bounded.
 *   - part URLs are presigned UP FRONT in batches (`PRESIGN_BATCH_SIZE` per API
 *     round-trip) instead of one presign call per part.
 *
 * Retry model (in-session only, per plan "retry individual parts"):
 *   - each part PUT is attempted up to `maxPartAttempts` times.
 *   - on retry we RE-presign that single part (batch-of-one), so a retry after
 *     the up-front presigned URL is rejected/expired (403/TTL) still works — this
 *     preserves the per-attempt re-presign safety the serial loop had.
 * Cross-reload resume is intentionally out of scope for this cut.
 *
 * `partSizeBytes` is a client hint only — the API decides the authoritative
 * part size and returns it in the initiate response; we chunk by that.
 */
export function startR2MultipartUpload(params: {
  file: File
  thumbnail?: File | null
  partSizeBytes: number
  maxPartAttempts?: number
  onProgress?: (progress: R2UploadProgress) => void
}): R2MultipartHandle {
  const { file, thumbnail, partSizeBytes, onProgress } = params
  const maxPartAttempts = params.maxPartAttempts ?? 3

  const controller = new AbortController()
  let aborted = false
  // Captured as soon as `initiate` resolves so abort() can clean up the R2
  // multipart upload even while parts are still in flight.
  let pendingSessionId: string | undefined

  const run = async (): Promise<R2UploadResult> => {
    const contentType = file.type || 'video/mp4'

    // 1) initiate: API reserves clipId + mediaToken + uploadId + session and
    //    presigns the optional thumbnail PUT.
    const initiateBody: R2InitiateRequest = {
      filename: file.name,
      contentType,
      sizeBytes: file.size,
      ...(thumbnail ? { thumbnailContentType: thumbnail.type || 'image/jpeg' } : {}),
    }
    const { data: session } = await axiosInstance.post<R2InitiateResponse>(
      R2_UPLOAD_ENDPOINTS.initiate,
      initiateBody,
      { signal: controller.signal },
    )
    pendingSessionId = session.uploadSessionId

    const effectivePartSize = session.partSizeBytes || partSizeBytes
    const totalParts = splitIntoParts(file.size, effectivePartSize)

    const parts: R2PartProgress[] = Array.from(
      { length: totalParts },
      (_, i) => ({
        partNumber: i + 1,
        loaded: 0,
        total: Math.min(effectivePartSize, file.size - i * effectivePartSize),
        status: 'pending',
      }),
    )

    const emit = () => {
      if (!onProgress) return
      const uploadedBytes = parts.reduce((sum, p) => sum + p.loaded, 0)
      onProgress({
        percent: file.size > 0 ? (uploadedBytes / file.size) * 100 : 0,
        uploadedBytes,
        totalBytes: file.size,
        parts: parts.map((p) => ({ ...p })),
      })
    }

    emit()

    // 2) optional thumbnail: PUT to the presigned URL from initiate (freshest
    //    here). Small file; not tracked in the source-part progress bar.
    let thumbnailInputKey: string | undefined
    if (thumbnail && session.thumbnailUploadUrl) {
      await axios.put(session.thumbnailUploadUrl, thumbnail, {
        signal: controller.signal,
        headers: { 'Content-Type': thumbnail.type || 'image/jpeg' },
      })
      thumbnailInputKey = session.thumbnailInputKey
    }

    // 3) presign all parts UP FRONT in batches (one round-trip per batch instead
    //    of one per part), then upload through a bounded worker pool.
    const presignedUrls = new Map<number, string>()
    for (const batch of chunkPartNumbers(totalParts, PRESIGN_BATCH_SIZE)) {
      if (aborted) throw new DOMException('aborted', 'AbortError')
      const presignBody: R2PresignPartsRequest = {
        uploadSessionId: session.uploadSessionId,
        partNumbers: batch,
      }
      const { data: presigned } =
        await axiosInstance.post<R2PresignPartsResponse>(
          R2_UPLOAD_ENDPOINTS.presignParts,
          presignBody,
          { signal: controller.signal },
        )
      for (const p of presigned.parts) {
        presignedUrls.set(p.partNumber, p.url)
      }
    }

    // Re-presign a single part (batch-of-one). Used both for an up-front URL the
    // API omitted and as the retry path when an up-front URL is rejected/expired
    // (403/TTL) — this preserves the per-attempt re-presign safety the old serial
    // loop relied on, now that we presign ahead of time.
    const presignSinglePart = async (partNumber: number): Promise<string> => {
      const presignBody: R2PresignPartsRequest = {
        uploadSessionId: session.uploadSessionId,
        partNumbers: [partNumber],
      }
      const { data: presigned } =
        await axiosInstance.post<R2PresignPartsResponse>(
          R2_UPLOAD_ENDPOINTS.presignParts,
          presignBody,
          { signal: controller.signal },
        )
      const url = presigned.parts.find((p) => p.partNumber === partNumber)?.url
      if (!url) {
        throw new Error(`API did not presign part ${partNumber}`)
      }
      presignedUrls.set(partNumber, url)
      return url
    }

    // Pre-sized by index so completion (in finish order) stays sorted by
    // partNumber — R2 complete requires ascending parts.
    const uploadedByIndex: (R2UploadedPart | undefined)[] = new Array(totalParts)

    const uploadOnePart = async (partNumber: number): Promise<void> => {
      const partState = parts[partNumber - 1]
      const start = (partNumber - 1) * effectivePartSize
      // Slice the Blob here (when the worker pulls this part) — not up front —
      // so live slices in memory stay bounded by the pool concurrency.
      const blob = file.slice(start, start + partState.total)

      let lastError: unknown
      for (let attempt = 1; attempt <= maxPartAttempts; attempt += 1) {
        if (aborted) throw new DOMException('aborted', 'AbortError')
        partState.status = 'uploading'
        partState.loaded = 0
        emit()
        try {
          // First attempt uses the up-front presigned URL; on retry (e.g. a
          // 403/expired URL), re-presign this single part.
          const partUrl =
            attempt === 1 && presignedUrls.has(partNumber)
              ? presignedUrls.get(partNumber)!
              : await presignSinglePart(partNumber)

          // Bare axios.put — bypasses axiosInstance baseURL + 401 interceptor.
          // Content-Type here must match what the API signed on the part URL,
          // or R2 returns 403 SignatureDoesNotMatch. (Reconciled in Phase 5
          // against the deployed R2 signing config.)
          const putRes = await axios.put(partUrl, blob, {
            signal: controller.signal,
            headers: { 'Content-Type': contentType },
            onUploadProgress: (e) => {
              partState.loaded = e.loaded
              emit()
            },
          })

          uploadedByIndex[partNumber - 1] = {
            partNumber,
            etag: extractEtag(
              putRes.headers as unknown as Record<string, unknown>,
            ),
          }
          partState.loaded = partState.total
          partState.status = 'done'
          emit()
          return
        } catch (err) {
          lastError = err
          if (axios.isCancel(err) || aborted) {
            throw err
          }
          partState.status = 'error'
          emit()
        }
      }

      throw lastError instanceof Error
        ? lastError
        : new Error(`R2 part ${partNumber} failed`)
    }

    // Bounded worker pool: N workers pull the next part from a shared cursor.
    // Reading + incrementing the cursor is atomic in single-threaded JS (no
    // `await` between read and increment). On the first failure we stop pulling
    // new parts so the pool drains quickly, then let the rejection propagate.
    let cursor = 0
    let poolFailed = false
    const worker = async (): Promise<void> => {
      for (;;) {
        if (poolFailed || aborted) return
        const partNumber = cursor + 1
        if (partNumber > totalParts) return
        cursor += 1
        try {
          await uploadOnePart(partNumber)
        } catch (err) {
          poolFailed = true
          // Cancel sibling in-flight PUTs immediately so a hard part failure
          // doesn't keep consuming the uplink while the rejection propagates.
          // (handle.abort() in the caller still runs the API-side multipart abort.)
          controller.abort()
          throw err
        }
      }
    }

    const workerCount = Math.min(PART_UPLOAD_CONCURRENCY, totalParts)
    await Promise.all(Array.from({ length: workerCount }, () => worker()))

    const uploaded = uploadedByIndex.filter(
      (p): p is R2UploadedPart => p !== undefined,
    )
    if (uploaded.length !== totalParts) {
      throw new Error(
        `R2 upload incomplete: ${uploaded.length}/${totalParts} parts`,
      )
    }

    // 4) complete the multipart upload (parts ascending by partNumber).
    const completeBody: R2CompleteRequest = {
      uploadSessionId: session.uploadSessionId,
      parts: uploaded,
    }
    await axiosInstance.post(R2_UPLOAD_ENDPOINTS.complete, completeBody, {
      signal: controller.signal,
    })

    return {
      uploadSessionId: session.uploadSessionId,
      inputKey: session.inputKey,
      thumbnailInputKey,
    }
  }

  const abort = async (): Promise<void> => {
    if (aborted) return
    aborted = true
    controller.abort()
    // Best-effort: tell the API to abort the R2 multipart upload so no orphan
    // parts linger. We do not have the uploadSessionId until initiate resolves,
    // so abort() before initiate just cancels the in-flight request.
    try {
      const sessionId = pendingSessionId
      if (sessionId) {
        const abortBody: R2AbortRequest = { uploadSessionId: sessionId }
        await axiosInstance.post(R2_UPLOAD_ENDPOINTS.abort, abortBody)
      }
    } catch (err) {
      // Abort cleanup is best-effort; an R2 lifecycle rule (Appendix C) also
      // reaps aborted multipart uploads after 1 day.
      console.error('R2 abort cleanup failed', err)
    }
  }

  return {
    done: run(),
    abort,
  }
}
